import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'

export function useRecordingDuration({
  recordingStartedAt
}: {
  recordingStartedAt: string | undefined | null
}) {
  const [isStarted, setIsStarted] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [pauseDuration, setPauseDuration] = useState(0)
  const recordingStartTimeRef = useRef<DateTime | null>(null)
  const intervalIdRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const pauseStartTimeRef = useRef<DateTime | null>(null)

  // Helper function to calculate and set duration
  const updateDuration = () => {
    if (recordingStartTimeRef.current) {
      const currentDuration = DateTime.now().diff(
        recordingStartTimeRef.current,
        'seconds'
      ).seconds

      setRecordingDuration(Math.floor(currentDuration - pauseDuration))
    }
  }

  // Initialize recording start time from recordingStartedAt
  const initializeStartTime = () => {
    if (recordingStartedAt && !recordingStartTimeRef.current) {
      recordingStartTimeRef.current = DateTime.fromISO(recordingStartedAt)
      updateDuration()
    }
  }

  useEffect(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = undefined
    }

    if (isStarted) {
      if (!recordingStartTimeRef.current) {
        recordingStartTimeRef.current = DateTime.now()
      }

      intervalIdRef.current = setInterval(updateDuration, 1000)
    }

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current)
        intervalIdRef.current = undefined
      }
    }
  }, [isStarted])

  const beginRecordingTimer = () => {
    initializeStartTime()
    setIsStarted(true)
  }

  const stopRecordingTimer = () => {
    if (isStarted) {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current)
        intervalIdRef.current = undefined
      }
      setIsStarted(false)
    }
  }

  const pauseRecordingTimer = () => {
    if (!isStarted) return
    stopRecordingTimer()
    pauseStartTimeRef.current = DateTime.now()
  }

  const resumeRecordingTimer = () => {
    if (pauseStartTimeRef.current) {
      const exactPauseDuration = DateTime.now().diff(
        pauseStartTimeRef.current,
        'seconds'
      ).seconds

      setPauseDuration(prev => prev + exactPauseDuration)
      pauseStartTimeRef.current = null
      beginRecordingTimer()
    }
  }

  const resetRecordingTimer = () => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = undefined
    }
    setIsStarted(false)
    setRecordingDuration(0)
    recordingStartTimeRef.current = null
  }

  const hasFiveMinutesPassed = recordingDuration > 300

  return {
    recordingDuration,
    beginRecordingTimer,
    stopRecordingTimer,
    resetRecordingTimer,
    pauseRecordingTimer,
    resumeRecordingTimer,
    hasFiveMinutesPassed
  }
}
